import { FC, useMemo } from 'react';
import classNames from 'classnames';

import Expandable from '@z-components/atoms/Expandable';
import CaseStudyPhotos from '@z-components/molecules/CaseStudyPhotos';

import type { CaseStudy, CaseStudyPhotoWithLocalizedTexts } from './types';
import styles from './CaseStudyCardProProfile.module.scss';

const NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW = 5;
const INFO_LIST_LINE_HEIGHT = 40;
const INFO_LIST_SHOW_MORE_MARGIN = 30;
const INFO_LIST_MIN_HEIGHT =
  NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW * INFO_LIST_LINE_HEIGHT + INFO_LIST_SHOW_MORE_MARGIN;
const INFO_MESSAGE_LENGTH: Record<string, number> = {
  en: 125,
  ja: 61,
};

const SUMMARY_MIN_HEIGHT = 72;

const THUMBNAIL_HEIGHT = 100;

const PHOTOS_PER_ROW = 2;

const TRANSLATIONS = {
  en: {
    basicInfo: 'Basic information',
    readMore: '...Read More',
  },
  ja: {
    basicInfo: '基本情報',
    readMore: '. . . もっと読む',
  },
};

// For personal trainers, do not display the second line. Note that this is temporary.
const shouldDisplayBasicInfo = (jobtypeId: string, index: number) =>
  jobtypeId !== 'personal-trainer' || index !== 1;

export interface Props {
  caseStudy: CaseStudy;
  hasBoxShadow?: boolean;
  language?: 'en' | 'ja';
}

const CaseStudyCardProProfile: FC<Props> = ({
  caseStudy: { title, summary, photos, basicInfoList = [], jobTypeId },
  hasBoxShadow = true,
  language = 'ja',
}) => {
  const infoListExpanded = useMemo(
    () => basicInfoList.length <= NUM_OF_INFO_LIST_LINES_ABLE_TO_SHOW,
    [basicInfoList]
  );

  const photosWithLocalizedCaptions = useMemo(
    () =>
      photos?.reduce((allPhotos, photo) => {
        if (photo.photoUrl) {
          return allPhotos.concat({
            altText: photo.altText,
            description: photo.description,
            url: photo.photoUrl,
          });
        }
        return allPhotos;
      }, [] as Array<CaseStudyPhotoWithLocalizedTexts>),
    [photos]
  );

  return (
    <div
      className={classNames(styles.container, {
        [styles['with-shadow']]: hasBoxShadow,
        [styles['no-shadow']]: !hasBoxShadow,
      })}
      data-test-id="case-study-card"
    >
      <div className={styles.title}>{title}</div>
      <Expandable
        className={styles.summary}
        defaultExpanded={(summary?.length ?? 0) <= INFO_MESSAGE_LENGTH[language]}
        expandText={TRANSLATIONS[language].readMore}
        expandClassname={classNames(styles.more, styles['bottom-right'])}
        minHeight={SUMMARY_MIN_HEIGHT}
        hasIcon={false}
      >
        <pre className={styles['molded-sentences']}>{summary ?? ''}</pre>
      </Expandable>
      <div className={styles.hr} />
      <CaseStudyPhotos
        rowLimit={PHOTOS_PER_ROW}
        thumbnailHeight={THUMBNAIL_HEIGHT}
        photos={photosWithLocalizedCaptions}
      />
      <Expandable
        defaultExpanded={infoListExpanded}
        minHeight={INFO_LIST_MIN_HEIGHT}
        title={TRANSLATIONS[language].basicInfo}
        expandClassname={classNames(styles.more, styles['bottom-right'], styles['full-width'])}
      >
        <div className={styles['basic-info-table']}>
          {basicInfoList.map(
            (basicInfo, index) =>
              shouldDisplayBasicInfo(jobTypeId, index) && (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`basic-info-${index}`} className={classNames(styles['table-row'])}>
                  <div
                    className={classNames(
                      styles['basic-info-table-cell'],
                      styles['basic-info-label']
                    )}
                  >
                    {basicInfo.title}
                  </div>
                  <div
                    className={classNames(
                      styles['basic-info-table-cell'],
                      styles['basic-info-value']
                    )}
                  >
                    {basicInfo.description}
                  </div>
                </div>
              )
          )}
        </div>
      </Expandable>
    </div>
  );
};

export default CaseStudyCardProProfile;
export type { Props as CaseStudyCardProProfileProps };
